import type { CSSProperties, FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { cn } from '@/lib/cn';

/**
 * Tumble's footer grid, by how many link columns stand beside the wordmark.
 * Written out because Tailwind reads class names out of the source and never
 * sees a string built at runtime.
 */
const TUMBLE_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[2fr_1fr]',
  2: 'lg:grid-cols-[2fr_1fr_1fr]',
  3: 'lg:grid-cols-[2fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]',
};

/** The footer's copy colour: cream, read against the violet ground. */
const CREAM = '#fffaf3';

/**
 * The band's own names for the colours inside it, so a shared piece that
 * paints itself in "primary" or "text" - the language switcher - comes out
 * cream on violet rather than violet on violet.
 */
const BAND_STYLE = {
  '--storefront-primary': CREAM,
  '--storefront-text': CREAM,
  '--storefront-background': 'var(--storefront-palette-primary)',
  '--storefront-surface': `color-mix(in srgb, ${CREAM} 22%, var(--storefront-palette-primary))`,
  backgroundColor: 'var(--storefront-palette-primary)',
  color: `${CREAM}d9`,
} as CSSProperties;

/** One footer column: a rounded heading over its links. */
function TumbleColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-xl font-semibold leading-tight"
        style={{ fontFamily: 'var(--storefront-heading-font)', color: CREAM }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The violet close to a cream page, its top corners rounded off like the lid
 * of a toy box.
 *
 * The band is set on the merchant's primary, so it moves with their palette -
 * which is also why its copy is a fixed cream rather than the page colour: a
 * shop that repaints its background white still wants its footer copy light.
 * The big rounded wordmark and the shop's line on the left, then the footer
 * menu, the shelves and the pages, each skipped when empty and the grid sized
 * to what is left, with the fine print under a hairline.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer className="rounded-t-[2.5rem] pt-16 text-[15px]" style={BAND_STYLE}>
      <StorefrontContainer
        className={cn('grid gap-10 pb-14 text-start sm:grid-cols-2', TUMBLE_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-5xl font-bold leading-none"
            style={{ fontFamily: 'var(--storefront-heading-font)', color: CREAM }}
          >
            {storeName}
          </p>
          {description ? <p className="mt-5 max-w-[24rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <TumbleColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:text-[color:var(--storefront-accent)]"
                />
              ))}
            </nav>
          </TumbleColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <TumbleColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-2.5"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="hover:text-[color:var(--storefront-accent)]"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </TumbleColumn>
        ))}
        {preview ? null : (
          <TumbleColumn title={copy('theme.footer_support')}>
            <PageLinks className="mb-2.5 flex-col items-start justify-start gap-2.5" />
            <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </TumbleColumn>
        )}
      </StorefrontContainer>
      <StorefrontContainer>
        <div
          className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t py-6 text-[13px]"
          style={{ borderColor: `${CREAM}40` }}
        >
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          <span style={copyrightStyle(footer)}>
            © {new Date().getFullYear()} {storeName} ·{' '}
            {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </div>
      </StorefrontContainer>
    </footer>
  );
};
